import React, { useState, useEffect } from 'react';
import AIPickCard from './components/AIPickCard';
import StatsBar from './components/StatsBar';
import useLiveUpdates from './hooks/useLiveUpdates';
import './DailyPicks.css';

const DailyPicks = ({ onAddToBetSlip, user, analytics }) => {
  const [picks, setPicks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPreviousPicks, setShowPreviousPicks] = useState(false);
  const [historicalPicks, setHistoricalPicks] = useState({});
  
  // Live updates hook
  const { liveOdds, lastUpdate, isLoading: liveLoading, refreshNow } = useLiveUpdates(true, 30000);

  useEffect(() => {
    loadTodaysPicks();
  }, []);

  // Real verification system
  const verifyPickResults = async (picks, gameDate) => {
    try {
      // Get game results for the specific date
      const response = await fetch(`https://7rp5uynwecws7kby4pzp6y6lme0irmkv.lambda-url.us-east-1.on.aws/game-results?date=${gameDate}`);
      const data = await response.json();
      
      if (!data.final_games || data.final_games.length === 0) {
        console.log('No final games found for verification');
        return picks; // Return original picks if no results available
      }
      
      const verifiedPicks = picks.map(pick => {
        // Find matching game
        const matchingGame = data.final_games.find(game => {
          const gameMatchup = `${game.away_team.name} @ ${game.home_team.name}`;
          return pick.matchup.includes(game.away_team.name) || 
                 pick.matchup.includes(game.home_team.name) ||
                 gameMatchup.toLowerCase().includes(pick.matchup.toLowerCase());
        });
        
        if (!matchingGame) {
          return pick; // Return original if no matching game found
        }
        
        const awayScore = matchingGame.away_team.score;
        const homeScore = matchingGame.home_team.score;
        const totalScore = awayScore + homeScore;
        
        // Verify the pick based on type
        let result = 'LOSS';
        let actualResult = '';
        
        if (pick.selection.includes('Over') || pick.selection.includes('Under')) {
          // Total bet verification
          const line = parseFloat(pick.selection.match(/[\d.]+/)[0]);
          if (pick.selection.includes('Over')) {
            result = totalScore > line ? 'WIN' : 'LOSS';
            actualResult = `Total: ${totalScore} (${totalScore > line ? 'Over' : 'Under'} ${line})`;
          } else {
            result = totalScore < line ? 'WIN' : 'LOSS';
            actualResult = `Total: ${totalScore} (${totalScore < line ? 'Under' : 'Over'} ${line})`;
          }
        } else if (pick.selection.includes('+') || pick.selection.includes('-')) {
          // Spread bet verification
          const spreadMatch = pick.selection.match(/([+-][\d.]+)/);
          if (spreadMatch) {
            const spread = parseFloat(spreadMatch[1]);
            const teamName = pick.selection.split(' ')[0];
            
            let teamScore, opponentScore;
            if (matchingGame.away_team.name.includes(teamName)) {
              teamScore = awayScore;
              opponentScore = homeScore;
            } else {
              teamScore = homeScore;
              opponentScore = awayScore;
            }
            
            const coverMargin = teamScore + spread - opponentScore;
            result = coverMargin > 0 ? 'WIN' : 'LOSS';
            actualResult = `${teamName} ${result === 'WIN' ? 'covered' : 'did not cover'} ${spread} (won by ${teamScore - opponentScore})`;
          }
        }
        
        // Calculate profit
        const profit = result === 'WIN' ? 
          (pick.odds > 0 ? pick.odds : (100 / Math.abs(pick.odds)) * 100) : 
          -100;
        
        return {
          ...pick,
          result,
          profit,
          finalScore: `${matchingGame.away_team.name} ${awayScore}, ${matchingGame.home_team.name} ${homeScore} (${actualResult})`
        };
      });
      
      return verifiedPicks;
    } catch (error) {
      console.error('Verification failed:', error);
      return picks; // Return original picks if verification fails
    }
  };

  const loadVerifiedResults = async () => {
    try {
      const response = await fetch('https://7rp5uynwecws7kby4pzp6y6lme0irmkv.lambda-url.us-east-1.on.aws/verify-picks');
      const data = await response.json();
      
      if (data.verified_picks && data.verified_picks.length > 0) {
        // Update historical picks with verified results
        console.log('Verified pick results:', data.verified_picks);
        // Could update state here if needed for real-time verification
      }
    } catch (error) {
      console.error('Error loading verified results:', error);
    }
  };

  const loadTodaysPicks = async () => {
    setLoading(true);
    try {
      // Use the real picks API with Aurora database
      const response = await fetch('https://sqhpzxuimundhzstmjqsr6w4dq0jyyia.lambda-url.us-east-1.on.aws/api/picks?days=7');
      
      if (!response.ok) {
        throw new Error('API not available');
      }
      
      const data = await response.json();
      
      if (data.picks && data.picks.length > 0) {
        // Separate pending picks (today's active) from settled picks (historical)
        const pendingPicks = data.picks
          .filter(p => p.result === 'pending')
          .map(p => ({
            id: p.pick_id,
            matchup: p.matchup,
            selection: p.selection,
            odds: p.odds,
            confidence: p.confidence,
            reasoning: p.rationale ? p.rationale[0] : '',
            game_time: p.game_time,
            date: p.date,
            result: p.result
          }));
        
        // Build historical picks from settled results
        const settledPicks = data.picks.filter(p => p.result !== 'pending');
        const historical = {};
        settledPicks.forEach(p => {
          const dateKey = p.date;
          if (!historical[dateKey]) {
            historical[dateKey] = {
              date: new Date(dateKey + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
              picks: [],
              record: '',
              profit: 0,
              roi: 0
            };
          }
          const isWin = p.result === 'won';
          const profit = isWin ? (p.odds > 0 ? p.odds : 100 / Math.abs(p.odds) * 100) : -100;
          historical[dateKey].picks.push({
            id: p.pick_id,
            matchup: p.matchup,
            selection: p.selection,
            odds: p.odds,
            result: isWin ? 'WIN' : 'LOSS',
            profit: profit,
            finalScore: ''
          });
        });
        // Calculate record/profit per day
        Object.keys(historical).forEach(dateKey => {
          const day = historical[dateKey];
          const wins = day.picks.filter(p => p.result === 'WIN').length;
          const losses = day.picks.filter(p => p.result === 'LOSS').length;
          day.record = `${wins}-${losses}`;
          day.profit = day.picks.reduce((sum, p) => sum + p.profit, 0);
          day.roi = day.picks.length > 0 ? (day.profit / (day.picks.length * 100) * 100).toFixed(1) : 0;
        });
        
        setPicks(pendingPicks);
        setHistoricalPicks(historical);
        setMessage(pendingPicks.length === 0 ? 'No active picks right now. Check previous picks below!' : '');
        
        // Update stats from API
        const wins = data.stats.wins;
        const losses = data.stats.losses;
        const totalSettled = wins + losses;
        const profit = (wins * 91) - (losses * 100);
        setStats({
          totalBets: totalSettled,
          totalWagered: totalSettled * 100,
          totalProfit: profit,
          winRate: data.stats.win_rate,
          roi: totalSettled > 0 ? (profit / (totalSettled * 100) * 100).toFixed(1) : 0
        });
      } else {
        setPicks([]);
        setMessage('No picks available today. Check back later!');
      }
    } catch (error) {
      console.error('Error loading picks:', error);
      setPicks([]);
      setMessage('No NBA games scheduled for today. Check back tomorrow!');
    }
    setLoading(false);
  };

  const addToBetSlip = (pick) => {
    if (onAddToBetSlip) {
      onAddToBetSlip(pick);
    } else {
      alert(`✅ Added ${pick.selection} to bet slip!`);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    const sign = amount >= 0 ? '+' : '';
    return `${sign}$${amount.toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="daily-picks">
      {/* Dashboard Stats at Top */}
      <div className="performance-dashboard">
        <h2>🤖 AI Edges Performance</h2>
        <div className="dashboard-stats">
          <div className="stat-item">
            <span className="stat-label">Total Bets</span>
            <span className="stat-value">{stats.totalBets}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Wagered</span>
            <span className="stat-value">${stats.totalWagered}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Profit/Loss</span>
            <span className={`stat-value ${stats.totalProfit >= 0 ? 'profit' : 'loss'}`}>
              {formatCurrency(stats.totalProfit)}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Win Rate</span>
            <span className="stat-value">{stats.winRate}%</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">ROI</span>
            <span className={`stat-value ${stats.roi >= 0 ? 'profit' : 'loss'}`}>
              {stats.roi}%
            </span>
          </div>
        </div>
      </div>

      {/* Today's Picks Section */}
      <div className="picks-header">
        <h3 className="todays-picks-title">Today's Picks</h3>
        <div className="header-info">
          <span className="games-date">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </div>
      </div>

      {/* Today's Picks or No Games Message */}
      {loading ? (
        <div className="loading">Loading today's picks...</div>
      ) : message ? (
        <div className="no-games-message">
          <p>{message}</p>
        </div>
      ) : (
        <div className="picks-grid">
          {picks.map(pick => (
            <AIPickCard 
              key={pick.id} 
              pick={pick} 
              onAddToBetSlip={addToBetSlip}
              liveOdds={liveOdds}
            />
          ))}
        </div>
      )}

      {/* Previous Picks Dropdown */}
      <div className="previous-picks-section">
        <div className="section-controls">
          <button 
            className="previous-picks-toggle"
            onClick={() => setShowPreviousPicks(!showPreviousPicks)}
          >
            📊 View Previous Picks {showPreviousPicks ? '▲' : '▼'}
          </button>
        </div>

        {showPreviousPicks && (
          <div className="previous-picks-content">
            {Object.entries(historicalPicks)
              .sort(([a], [b]) => new Date(b) - new Date(a)) // Sort by date descending
              .map(([date, dayData]) => (
                <div key={date} className="historical-day">
                  <div className="day-header">
                    <h4>{dayData.date}</h4>
                    <div className="day-summary">
                      <span className="day-record">{dayData.record}</span>
                      <span className={`day-profit ${dayData.profit >= 0 ? 'profit' : 'loss'}`}>
                        {formatCurrency(dayData.profit)}
                      </span>
                      <span className={`day-roi ${dayData.roi >= 0 ? 'profit' : 'loss'}`}>
                        {dayData.roi}% ROI
                      </span>
                    </div>
                  </div>
                  
                  <div className="historical-picks">
                    {dayData.picks.map(pick => (
                      <div key={pick.id} className={`historical-pick ${pick.result.toLowerCase()}`}>
                        <div className="pick-info">
                          <span className="pick-matchup">{pick.matchup}</span>
                          <span className="pick-selection">{pick.selection} ({pick.odds > 0 ? '+' : ''}{pick.odds})</span>
                        </div>
                        <div className="pick-result">
                          <span className={`result-badge ${pick.result.toLowerCase()}`}>
                            {pick.result === 'WIN' ? '✅' : '❌'} {pick.result}
                          </span>
                          <span className={`pick-profit ${pick.profit >= 0 ? 'profit' : 'loss'}`}>
                            {formatCurrency(pick.profit)}
                          </span>
                        </div>
                        <div className="final-score">{pick.finalScore}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      <StatsBar stats={stats} />
    </div>
  );
};

export default DailyPicks;
